import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import PropTypes from "prop-types"
import { toast } from "react-toastify"
import {
  getReportDownloadLink,
  getReportDownloads
} from "../../services/endpointUtils/hes"
import { openFileUrl } from "../../utils/downloadFile"
import { isPending, linkFrom, toNotifications } from "./reportDownloads"

/**
 * The download centre: the report queue, shared by the header bell and the
 * Notifications page.
 *
 * An export is a *request*, not a click that happens to save a file. That is
 * what the redesigned card says in words ("Download request from … on …"), and
 * it is literally how the API works: `POST /reports/downloads` answers `202`
 * with a `request_id`, the server builds the sheet in its own time, and
 * `GET /reports/downloads` is the queue it moves through. This holds that
 * queue so one place can show it however the user got here.
 *
 * Three calls, three jobs:
 *
 *   POST /reports/downloads        lodge a request
 *   GET  /reports/downloads        the queue — this feed
 *   GET  /reports/downloads/:id/link   where to collect a finished one
 */

const DownloadCenterContext = createContext(null)

/** How often to re-read the queue while something in it is still working. */
const POLL_MS = 10000

export const DownloadCenterProvider = ({ children, project, seed = [] }) => {
  const [items, setItems] = useState(seed)
  const [panelOpen, setPanelOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  /**
   * How to lodge each request again, keyed by request id.
   *
   * Filled when an export is made in this session, and the only thing a
   * Failed card's retry can use: re-lodging a request means knowing which
   * filters it carried, and this screen only knows that for the ones it made.
   * Held in a ref because a function on a state object would make the feed
   * unserialisable.
   */
  const runners = useRef(new Map())

  // One at a time: a poll landing on top of a manual refresh would show
  // whichever happened to finish last rather than the newer answer.
  const inFlight = useRef(false)

  const refresh = useCallback(async () => {
    if (inFlight.current) return
    inFlight.current = true
    setIsLoading(true)

    try {
      const response = await getReportDownloads()
      setItems(toNotifications(response, { project }))
      setError(null)
    } catch (err) {
      if (err?.code !== "ERR_CANCELED") {
        setError(err?.response?.data?.error_message || "Unable to read your downloads.")
      }
    } finally {
      inFlight.current = false
      setIsLoading(false)
    }
  }, [project])

  useEffect(() => {
    refresh()
  }, [refresh])

  /**
   * Keep reading the queue while anything in it is still being built.
   *
   * The server moves a request from PENDING to finished on its own schedule
   * and has no way to tell us, so the card can only change if we ask again.
   * The interval exists exactly as long as there is something to wait for —
   * an idle queue costs nothing.
   */
  const waiting = items.some(isPending)

  useEffect(() => {
    if (!waiting) return undefined
    const timer = window.setInterval(refresh, POLL_MS)
    return () => window.clearInterval(timer)
  }, [waiting, refresh])

  /**
   * Lodge a request, then show the queue it joined.
   *
   * The card isn't built here. The POST returns a receipt and the queue is
   * re-read, so what appears in the panel is the server's own row — there is
   * no local copy to drift from it, and no optimistic entry to reconcile with
   * the real one when it arrives.
   */
  const requestDownload = useCallback(
    async ({ run }) => {
      setPanelOpen(true)

      try {
        const receipt = await run()
        if (receipt?.requestId != null) runners.current.set(String(receipt.requestId), run)
        await refresh()
        return receipt
      } catch (err) {
        if (err?.code === "ERR_CANCELED") return null

        /**
         * Say it out loud.
         *
         * A request that was never lodged has no row in the queue, so there is
         * no card to carry this — without the toast, the press would look like
         * a button that did nothing.
         */
        const reason =
          err?.response?.data?.error_message ??
          err?.response?.data?.detail ??
          err?.message ??
          "That export didn’t go through."
        toast.error(reason, { hideProgressBar: true })
        return null
      }
    },
    [refresh]
  )

  /**
   * Collect a finished report.
   *
   * The link is fetched at the moment it is pressed rather than stored against
   * the row: these are presigned and expire, so one read when the list loaded
   * would be stale by the time anyone used it.
   */
  const openFile = useCallback(async (item) => {
    if (item?.requestId == null) return

    try {
      const response = await getReportDownloadLink(item.requestId)
      const link = linkFrom(response)
      if (!link) throw new Error("That report has no download link yet.")
      openFileUrl(link)
    } catch (err) {
      const reason =
        err?.response?.data?.error_message ?? err?.message ?? "Unable to open that report."
      toast.error(reason, { hideProgressBar: true })
    }
  }, [])

  /**
   * Ask for a failed report again.
   *
   * Only for a request this session made — retrying means re-sending the
   * filters, and a row from yesterday or another tab doesn't tell us what
   * they were. Rather than lodge a different request under the same name,
   * that case points back at the screen that can.
   */
  const retry = useCallback(
    async (item) => {
      const run = runners.current.get(String(item?.requestId))
      if (run) {
        await requestDownload({ run })
        return
      }
      toast.info("Run that export again from its own screen.", { hideProgressBar: true })
    },
    [requestDownload]
  )

  /**
   * Nothing is removed from the queue.
   *
   * The server owns the list and offers no delete, so a card that vanished
   * here would be back on the next poll. Nothing in the queue reports as
   * `downloading` either — a report is being built or it is finished — so the
   * cancel button the design draws for that state never comes up.
   */
  const dismiss = useCallback(() => {}, [])

  const openPanel = useCallback(() => setPanelOpen(true), [])
  const closePanel = useCallback(() => setPanelOpen(false), [])
  const togglePanel = useCallback(() => setPanelOpen((open) => !open), [])

  const value = useMemo(
    () => ({
      items,
      isLoading,
      error,
      refresh,
      panelOpen,
      openPanel,
      closePanel,
      togglePanel,
      requestDownload,
      openFile,
      retry,
      dismiss
    }),
    [
      items,
      isLoading,
      error,
      refresh,
      panelOpen,
      openPanel,
      closePanel,
      togglePanel,
      requestDownload,
      openFile,
      retry,
      dismiss
    ]
  )

  return <DownloadCenterContext.Provider value={value}>{children}</DownloadCenterContext.Provider>
}

DownloadCenterProvider.propTypes = {
  children: PropTypes.node,
  /** Shown on each card — the queue is scoped to one project by its header. */
  project: PropTypes.string,
  /** The feed to start from, before the first read. Used by tests. */
  seed: PropTypes.array
}

/**
 * The queue and its controls.
 *
 * Throws without a provider rather than degrading quietly: a screen that
 * silently dropped export requests on the floor would look like it was
 * working. `AppShell` provides it, so every routed screen has one.
 */
export const useDownloadCenter = () => {
  const value = useContext(DownloadCenterContext)
  if (!value) {
    throw new Error("useDownloadCenter must be used within a <DownloadCenterProvider>.")
  }
  return value
}

export default DownloadCenterContext
